import { useEffect, useState } from "react";
import { useAuth, homePathFor } from "@/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, MicrosoftOutlookLogo, Quotes } from "@phosphor-icons/react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TrakLogo } from "@/components/TrakLogo";

const SERVER_ROOM = "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ERROR_MESSAGES = {
  not_authorized: "This Microsoft account isn't authorized. Ask an admin to invite you to TRAK first.",
  no_email: "Couldn't read an email from your Microsoft account.",
  invalid_state: "Sign-in session expired. Please try again.",
  missing_state: "Sign-in session expired. Please try again.",
  microsoft_error: "Microsoft sign-in was cancelled or failed.",
  invalid_audience: "Sign-in token was not issued for this app.",
  ip_blocked: "Access from your network location is not allowed. Contact your administrator to allowlist your IP address.",
  suspended: "Your account has been suspended. Please contact your administrator to restore access.",
};

export default function LoginPage() {
  const { user, exchangeHandoff } = useAuth();
  const [config, setConfig] = useState({ microsoft_enabled: true });
  const [authError, setAuthError] = useState("");
  const [autoSignIn, setAutoSignIn] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/config").then(r => setConfig(r.data)).catch(() => {});
    // Handle redirect back from Microsoft (?ms=1 -> exchange httpOnly handoff cookie; ?error=...)
    const params = new URLSearchParams(window.location.search);
    const ms = params.get("ms");
    const error = params.get("error");
    if (ms === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      exchangeHandoff()
        .then(u => { toast.success("Signed in with Microsoft"); nav(homePathFor(u)); })
        .catch(() => setAuthError("Sign-in session expired. Please try signing in again."));
    } else if (error) {
      window.history.replaceState({}, "", window.location.pathname);
      setAuthError(ERROR_MESSAGES[error] || "Sign-in failed. Please try again.");
    } else if (params.get("auto") === "1") {
      // Unattended videowall: the 24h session lapsed — go straight back to Microsoft sign-in.
      window.history.replaceState({}, "", window.location.pathname);
      setAutoSignIn(true);
      setTimeout(() => { window.location.href = `${window.location.origin}/api/auth/microsoft/login`; }, 1200);
    }
  }, []);

  if (user) return <Navigate to={homePathFor(user)} replace />;

  const microsoftLogin = () => {
    window.location.href = `${window.location.origin}/api/auth/microsoft/login`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="absolute top-5 right-5 z-10">
        <ThemeToggle />
      </div>
      {/* Left: Auth */}
      <div className="flex items-center justify-center px-6 lg:px-16 py-12 bg-sidebar text-sidebar-foreground dark:bg-background dark:text-foreground">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="h-9 w-9 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm">
              <TrakLogo size={22} />
            </div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight">TRAK</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-sidebar-muted dark:text-muted-foreground font-mono">Service Operations Console</div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-3">
            Your first call.<br/>
            <span className="text-[hsl(212_55%_78%)]">Your last worry.</span>
          </h1>
          <p className="text-sm text-sidebar-muted dark:text-muted-foreground mb-8 max-w-sm">
            ITIL-aligned incident, problem, change and asset management — powered by AI. Sign in with your Microsoft work account.
          </p>

          {config.microsoft_enabled ? (
            <button
              onClick={microsoftLogin}
              data-testid="microsoft-login-btn"
              className="w-full h-12 rounded-sm bg-[#2f2f2f] hover:bg-[#1f1f1f] text-white font-mono text-sm flex items-center justify-center gap-3 transition-colors"
            >
              <MicrosoftOutlookLogo size={18} weight="fill" /> Sign in with Microsoft
            </button>
          ) : (
            <div data-testid="login-unavailable" className="rounded-sm border border-sidebar-border dark:border-border bg-white/5 dark:bg-muted/40 px-4 py-3 text-sm text-sidebar-muted dark:text-muted-foreground">
              Sign-in is temporarily unavailable. Please try again shortly or contact your administrator.
            </div>
          )}

          {authError && (
            <div data-testid="auth-error-banner" className="mt-4 flex items-start gap-2 rounded-sm border border-rag-red/40 bg-rag-red-bg px-3 py-2.5 text-sm text-rag-red-fg">
              <ShieldCheck size={16} className="mt-0.5 shrink-0" />
              <span>{authError}</span>
            </div>
          )}
          {autoSignIn && (
            <div data-testid="auto-signin-banner" className="mt-4 flex items-start gap-2 rounded-sm border border-border bg-accent/30 px-3 py-2.5 text-sm text-muted-foreground">
              <span className="h-2 w-2 mt-1.5 rounded-full bg-primary animate-pulse shrink-0" />
              <span>Videowall session ended after 24 hours — signing you in again with Microsoft…</span>
            </div>
          )}
        </div>
      </div>

      {/* Right: Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${SERVER_ROOM})` }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(216_54%_15%)]/90 via-[hsl(216_54%_15%)]/30 to-transparent" />
        <div className="absolute inset-0 grid-backdrop opacity-40" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div data-testid="login-testimonial" className="mb-8 rounded-sm border border-white/15 bg-white/10 backdrop-blur-md p-5 shadow-2xl">
              <Quotes size={20} weight="fill" className="text-[hsl(212_55%_78%)] mb-2" />
              <p className="text-sm leading-relaxed text-zinc-100">
                TRAK replaced three separate tools and cut our mean-time-to-resolve by 40%. Our analysts finally work from one screen.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-[hsl(212_55%_78%)]/20 border border-white/20 grid place-items-center text-[hsl(212_55%_78%)] font-display font-bold text-sm">M</div>
                <div className="text-xs font-mono leading-tight">
                  <div className="text-white">Maya Rele</div>
                  <div className="text-zinc-400">Head of IT Ops · Northwind</div>
                </div>
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(212_55%_78%)] font-mono mb-3">ITIL v4 · ISO 20000</div>
            <div className="font-display text-3xl font-bold leading-tight mb-3 text-white">Ship service excellence.</div>
            <p className="text-sm text-zinc-300">
              Configurable SLAs, full ticket lifecycle, and AI copilots for L1/L2 agents — in one control-room UI.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 font-mono text-xs">
              <div><div className="text-[hsl(212_55%_78%)] text-2xl">99.9%</div><div className="text-zinc-400">SLA target</div></div>
              <div><div className="text-[hsl(212_55%_78%)] text-2xl">4</div><div className="text-zinc-400">ITIL modules</div></div>
              <div><div className="text-[hsl(212_55%_78%)] text-2xl">∞</div><div className="text-zinc-400">Reports</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
